import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Staking } from './pages/Staking';
import { Governance } from './pages/Governance';
import { Auctions } from './pages/Auctions';
import BridgePage from './pages/Bridge';
import { Settings } from './pages/Settings';
import { WalletButton } from './components/Wallet';
import { SolarSystem } from './components/SolarSystem';
import { textInteractive } from './config/theme';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/staking', label: 'Staking' },
  { to: '/governance', label: 'Governance' },
  { to: '/auctions', label: 'Auctions' },
  { to: '/bridge', label: 'Bridge' },
  { to: '/stats', label: 'Stats' },
  { to: '/notifications', label: 'Notifications' },
];

function App(): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                {/* Mobile menu button */}
                <div className="flex items-center sm:hidden">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                    aria-controls="mobile-menu"
                    aria-expanded={mobileMenuOpen}
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    <span className="sr-only">Open main menu</span>
                    {mobileMenuOpen ? (
                      // X icon when menu is open
                      <svg
                        className="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    ) : (
                      // Hamburger icon when menu is closed
                      <svg
                        className="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="flex-shrink-0 flex items-center ml-2 sm:ml-0">
                  <h1 className="text-2xl font-bold text-primary-600">Gravity Station</h1>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`border-transparent ${textInteractive.link} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
              {/* Wallet Connect Button */}
              <div className="flex items-center">
                <WalletButton />
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            className={`${mobileMenuOpen ? 'block' : 'hidden'} sm:hidden`}
            id="mobile-menu"
          >
            <div className="pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/staking" element={<Staking />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/auctions" element={<Auctions />} />
            <Route path="/bridge" element={<BridgePage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/notifications" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function HomePage(): JSX.Element {
  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
        {/* Solar System Animation */}
        <div className="w-full max-w-2xl">
          <SolarSystem className="shadow-2xl" />
        </div>

        {/* Welcome Text */}
        <div className="text-center lg:text-left max-w-md">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to <span className="text-indigo-600">Gravity Station</span>
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Your unified interface for Gravity Bridge — connecting Ethereum and Cosmos ecosystems.
          </p>
          <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
            <Link
              to="/staking"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
            >
              Start Staking
            </Link>
            <Link
              to="/auctions"
              className="px-6 py-3 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
            >
              View Auctions
            </Link>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            Bridge assets, stake GRAV, vote on governance, and participate in token auctions.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatsPage(): JSX.Element {
  return (
    <div className="px-4 py-6 sm:px-0">
      <h2 className="text-2xl font-bold mb-4">Stats</h2>
      <p className="text-gray-600">Stats interface coming soon...</p>
    </div>
  );
}

export default App;
